/**
 * DataLayer Queue Manager
 *
 * Queues analytics events until Didomi is ready.
 * GTM consent mode handles whether tags actually fire based on consent.
 *
 * Features:
 * - Init-Safe: Creates dataLayer if missing
 * - Event-Driven: Waits for Didomi ready event
 * - Turbo-Compatible: No DOMContentLoaded dependencies
 * - Global State: Survives SPA navigations
 * - Bootstrap Queue: Handles events before manager loads
 *
 * Usage:
 *   window.consentDataLayer.queueOrPush(event);
 */

// Bootstrap queue: Catches early events before manager is ready
window._consentDataLayerBootstrap = window._consentDataLayerBootstrap || [];

class ConsentDataLayerManager {
    constructor() {
        window.dataLayer = window.dataLayer || [];

        /** @type {Array<object>} */
        this.queue = [];

        /** @type {boolean} */
        this.didomiReady = false;

        this.initDidomiListener();
        this.flushBootstrapQueue();

        console.debug(
            '[ConsentDataLayer] Manager initialized, waiting for Didomi ready',
        );
    }

    /**
     * Wait for Didomi to be ready, then flush queued events.
     * GTM consent mode handles consent decisions — we just wait for init.
     */
    initDidomiListener() {
        window.didomiOnReady = window.didomiOnReady || [];
        window.didomiOnReady.push(() => {
            console.debug('[ConsentDataLayer] Didomi ready - flushing queue');
            this.didomiReady = true;
            this.flushQueue();
        });
    }

    /**
     * Queue or push event based on Didomi ready state.
     *
     * @param {object} event - Analytics event payload
     */
    queueOrPush(event) {
        if (this.didomiReady) {
            console.debug('[ConsentDataLayer] Pushing event:', event.event);
            window.dataLayer.push(event);
        } else {
            console.debug('[ConsentDataLayer] Queueing event:', event.event);
            this.queue.push(event);
        }
    }

    /**
     * Flush all queued events to dataLayer.
     */
    flushQueue() {
        if (this.queue.length === 0) {
            return;
        }

        console.debug(
            `[ConsentDataLayer] Flushing ${this.queue.length} queued events`,
        );
        this.queue.forEach((event) => window.dataLayer.push(event));
        this.queue = [];
    }

    /**
     * Flush bootstrap queue (events that arrived before manager was ready).
     */
    flushBootstrapQueue() {
        if (window._consentDataLayerBootstrap?.length > 0) {
            console.debug(
                '[ConsentDataLayer] Flushing bootstrap queue:',
                window._consentDataLayerBootstrap.length,
                'events',
            );
            window._consentDataLayerBootstrap.forEach((event) =>
                this.queueOrPush(event),
            );
            window._consentDataLayerBootstrap = [];
        }
    }

    /**
     * Get current queue size (for debugging).
     * @returns {number}
     */
    getQueueSize() {
        return this.queue.length;
    }

    /**
     * Check if Didomi is ready (for debugging).
     * @returns {boolean}
     */
    isReady() {
        return this.didomiReady;
    }
}

// ============================================================================
// Global Initialization
// ============================================================================

if (!window.consentDataLayer) {
    window.consentDataLayer = new ConsentDataLayerManager();
}
